import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type {
  FieldValues,
  Path,
  DefaultValues,
  RegisterOptions,
  FieldErrors,
  UseFormReturn,
  Resolver,
  SubmitHandler,
} from 'react-hook-form';
import type { ZodTypeAny } from 'zod';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Combobox } from '@/components/ui/combobox';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import {
  FormField,
  SectionDivider,
  fieldErrorClass,
} from './onboarding-layout';

export type FieldType = 'text' | 'password' | 'number' | 'combobox';

export interface SelectOption {
  value: string;
  label: string;
}

export interface FieldConfig<T extends FieldValues> {
  name: Path<T>;
  label: string;
  type: FieldType;
  placeholder?: string;
  validation?: RegisterOptions<T, Path<T>>;
  options?: SelectOption[];
  disabled?: boolean;
  multiple?: boolean;
  sectionBefore?: string;
  actionButton?: React.ReactNode;
  loading?: boolean;
  layout?: 'default' | 'pat';
  onValueChange?: (val: string) => void;
}

interface UseOnboardingFormOptions<T extends FieldValues> {
  fields: FieldConfig<T>[];
  defaultValues?: DefaultValues<T>;
  onSubmit: (data: T) => void | Promise<void>;
  schema?: ZodTypeAny;
}

export interface UseOnboardingFormReturn<T extends FieldValues> {
  renderFields: (names?: Path<T>[]) => React.ReactNode;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  errors: FieldErrors<T>;
  isSubmitting: boolean;
  reset: UseFormReturn<T>['reset'];
  watch: UseFormReturn<T>['watch'];
  setValue: UseFormReturn<T>['setValue'];
  setError: UseFormReturn<T>['setError'];
  trigger: UseFormReturn<T>['trigger'];
  control: UseFormReturn<T>['control'];
}

export const FieldError = ({ message }: { message?: string }) =>
  message ? (
    <p role="alert" className={fieldErrorClass}>
      {message}
    </p>
  ) : null;

export function useOnboardingForm<T extends FieldValues>({
  fields,
  defaultValues,
  onSubmit,
  schema,
}: UseOnboardingFormOptions<T>): UseOnboardingFormReturn<T> {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
    setError,
    trigger,
  } = useForm<T>({
    defaultValues,
    mode: 'onTouched',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: schema ? (zodResolver(schema as any) as Resolver<T>) : undefined,
  });

  const renderFields = (names?: Path<T>[]): React.ReactNode => (
    <>
      {(names ? fields.filter(f => names.includes(f.name)) : fields).map(
        field => {
          const errorMsg = errors[field.name]?.message as string | undefined;

          const inputControl =
            field.type === 'combobox' ? (
              <Controller
                name={field.name}
                control={control}
                rules={schema ? undefined : field.validation}
                render={({ field: f }) => (
                  <Combobox
                    data={field.options ?? []}
                    multiple={field.multiple}
                    showMultipleTags={field.multiple}
                    value={
                      field.multiple
                        ? ((f.value as string[]) ?? [])
                        : ((f.value as string) ?? '')
                    }
                    onSelect={(
                      val: string | number | (string | number)[],
                    ) => {
                      f.onChange(val);
                      if (!field.multiple && field.onValueChange) {
                        field.onValueChange(String(val));
                      }
                    }}
                    placeholder={
                      field.placeholder ?? `Select ${field.label}`
                    }
                    disabled={field.disabled}
                    error={!!errors[field.name]}
                  />
                )}
              />
            ) : field.type === 'password' ? (
              <Controller
                name={field.name}
                control={control}
                rules={schema ? undefined : field.validation}
                render={({ field: f }) => (
                  <PasswordInput
                    value={String(f.value ?? '')}
                    onChange={val => f.onChange(val)}
                    onBlur={f.onBlur}
                    disabled={field.disabled}
                    placeholder={field.placeholder}
                    error={!!errors[field.name]}
                  />
                )}
              />
            ) : (
              <Controller
                name={field.name}
                control={control}
                rules={schema ? undefined : field.validation}
                render={({ field: f }) => (
                  <Input
                    type={field.type === 'number' ? 'text' : field.type}
                    placeholder={field.placeholder}
                    value={String(f.value ?? '')}
                    onChange={e =>
                      f.onChange(
                        field.type === 'number'
                          ? Number(e.target.value)
                          : e.target.value,
                      )
                    }
                    onBlur={f.onBlur}
                    disabled={field.disabled}
                    className={cn(
                      errors[field.name] &&
                        'border-[#fecdca] focus-visible:ring-[#fecdca]',
                    )}
                  />
                )}
              />
            );

          return (
            <React.Fragment key={String(field.name)}>
              {field.sectionBefore ? (
                <SectionDivider label={field.sectionBefore} />
              ) : null}
              <FormField
                label={field.label}
                error={errorMsg}
                layout={field.layout}
                loading={field.loading ? <Spinner compact /> : undefined}
                actionRow={field.actionButton}>
                {inputControl}
              </FormField>
            </React.Fragment>
          );
        },
      )}
    </>
  );

  return {
    renderFields,
    onSubmit: handleSubmit(onSubmit as SubmitHandler<T>),
    errors,
    isSubmitting,
    reset,
    watch,
    setValue,
    setError,
    trigger,
    control,
  };
}
